import { ValueIconName } from "@ppb/the-wall-icons";
import { BettingState, PlaceResult } from "@ppb/betslip-core";
import { PRICE_BOOST_MODE } from "@ppb/betslip-core/src/enums/price-boost-mode";
import { MONEY_BACK_MODE } from "@ppb/betslip-core/src/enums/money-back-mode";
import { GenerosityIconName } from "@ppb/the-wall-common/types/Betslip/GenerosityIcon.types";
import { currencyFormatWithDecimalPlaces } from "../formatters/currency-adapter";
import { formatFixedOdds } from "../formatters/odds-adapter";
import type { BetslipLocalization, BetslipOddsDisplay } from "../contract";
import { i18n } from "./i18n";
import { TranslationKey } from "../translations/keys";

export type GenerosityWalletType =
  | "ACCA_INSURANCE_TOKEN"
  | "BONUS_CASH"
  | "GHOST_LEG_TOKEN"
  | "MONEY_BACK_TOKEN"
  | "PRICE_BOOST_TOKEN";

export const sumWalletsAmounts = (value1 = 0, value2 = 0): number =>
  value1 || value2 ? (value1 * 100 + value2 * 100) / 100 : 0;

const getPlaceWithEnding = (place: number) => {
  const keyMap: Record<number, TranslationKey> = {
    2: "I18N.MONEY_BACK_REWARD.PLACED_SECOND",
    3: "I18N.MONEY_BACK_REWARD.PLACED_THIRD",
  };

  const key = keyMap[place] ?? "I18N.MONEY_BACK_REWARD.PLACED_OTHER";
  return `${place}${i18n({ key })}`;
};

export const getPlacesListed = (maxFinPos?: number | null) => {
  if (!maxFinPos || maxFinPos < 2) {
    return "";
  }

  const normalSeparator = ", ";
  const finalSeparator = ` ${i18n({ key: "I18N.MONEY_BACK_REWARD.SEPARATOR" })} `;

  const placesLabels = Array.from({ length: maxFinPos - 1 }, (_, idx) => getPlaceWithEnding(idx + 2));

  const places = placesLabels.map((placeLabel, idx) => {
    if (idx === 0) {
      return placeLabel;
    }

    const isLastLabel = idx === placesLabels.length - 1;
    const labelSeparator = isLastLabel ? finalSeparator : normalSeparator;

    return `${labelSeparator}${placeLabel}`;
  });

  return places.join("");
};

export const pickMoneyBackAlertMessage = ({
  maxFinPos,
  formattedAmount,
  isRefundWithCash,
}: {
  maxFinPos?: number | null;
  formattedAmount: string;
  isRefundWithCash?: boolean;
}) => {
  if (maxFinPos) {
    const placesListed = getPlacesListed(maxFinPos);
    return i18n({
      key: isRefundWithCash ? "I18N.MONEY_BACK_REWARD.RUN_PLACES.CASH" : "I18N.MONEY_BACK_REWARD.RUN_PLACES",
      interpolationValues: { places: placesListed, amount: formattedAmount },
    });
  }

  return i18n({
    key: isRefundWithCash ? "I18N.MONEY_BACK_REWARD.HORSE_LOSES.CASH" : "I18N.MONEY_BACK_REWARD.HORSE_LOSES",
    interpolationValues: { amount: formattedAmount },
  });
};

export const buildFreeBetsAlertMessage = ({
  localization,
  combinationAmount,
  numLines = 1,
  combinationAmountPerLine,
}: {
  localization: BetslipLocalization;
  combinationAmount?: number;
  numLines?: number;
  combinationAmountPerLine?: number;
}): string | undefined => {
  const isMultiLine = numLines > 1;

  const messageAmount = isMultiLine ? combinationAmountPerLine : combinationAmount;

  if (!messageAmount) {
    return undefined;
  }

  const formattedAmountLabel = currencyFormatWithDecimalPlaces({
    ...localization,
    value: messageAmount,
    decimalPlaces: 2,
  });

  const freeBetsWalletsAmountsLabel = isMultiLine ? `${numLines} x ${formattedAmountLabel}` : formattedAmountLabel;

  return i18n({
    key: "I18N.LABEL.BETSLIP_FREEBETS",
    interpolationValues: { bonus: freeBetsWalletsAmountsLabel },
  });
};

export const getGenerosityBetslipAlertData = ({
  combination,
  isAccaInsuranceTokenSelected,
  isGhostLegTokenSelected,
  isFreeBetsSelected,
  isPriceBoostSelected,
  isMoneyBackTokenSelected,
  isSingleBetBetslip,
  localization,
  numLines,
  combinationAmountPerLine,
  combinationAmount,
  ghostLegs,
  ghostLegMaxStake,
  oddsDisplayPreference,
}: {
  combination: BettingState.Combination;
  isAccaInsuranceTokenSelected: boolean;
  isGhostLegTokenSelected: boolean;
  isFreeBetsSelected: boolean;
  isPriceBoostSelected: boolean;
  isMoneyBackTokenSelected: boolean;
  isSingleBetBetslip: boolean;
  localization: BetslipLocalization;
  numLines: number;
  combinationAmountPerLine: number | undefined;
  combinationAmount: number | undefined;
  ghostLegs?: number;
  ghostLegMaxStake?: number;
  oddsDisplayPreference: BetslipOddsDisplay;
}): {
  message?: string;
  icon?: GenerosityIconName;
  type?: GenerosityWalletType;
} => {
  const { accaInsuranceNumberOfLegs, accaInsuranceAmountLimit, moneyBackNumberOfPlaces, moneyBackAmountLimit } =
    combination;

  if (isAccaInsuranceTokenSelected && accaInsuranceNumberOfLegs && accaInsuranceAmountLimit) {
    const formattedAccaInsuranceAmountLimit = currencyFormatWithDecimalPlaces({
      ...localization,
      value: accaInsuranceAmountLimit,
      decimalPlaces: 2,
    });

    const accaInsuranceOffer = combination.accaInsuranceOffers?.find(
      (offer) => offer.tokenId === combination.accaInsuranceTokenId,
    );

    return {
      message: i18n({
        key:
          accaInsuranceOffer?.mode === MONEY_BACK_MODE.CASH
            ? "I18N.MONEY_BACK_ACCA_CONDITION.CASH"
            : "I18N.MONEY_BACK_ACCA_CONDITION",
        interpolationValues: { numberOfLegs: accaInsuranceNumberOfLegs, value: formattedAccaInsuranceAmountLimit },
      }),
      icon: ValueIconName.MONEY_BACK,
      type: "ACCA_INSURANCE_TOKEN",
    };
  }

  if (isPriceBoostSelected) {
    const priceBoostOffer = combination.priceBoostOffers?.find(
      (offer) => offer.tokenId === combination.priceBoostTokenId,
    );

    if (priceBoostOffer?.mode === PRICE_BOOST_MODE.FIXED_ODDS && priceBoostOffer.odds?.decimalOdds) {
      const formattedFixedOdds = formatFixedOdds(priceBoostOffer.odds?.decimalOdds, oddsDisplayPreference);

      return {
        message: i18n({
          key: "I18N.BOOST_FIXED_ODDS_VALUE_APPLIED",
          interpolationValues: {
            value: formattedFixedOdds,
          },
        }),
        icon: ValueIconName.BOOSTER,
        type: "PRICE_BOOST_TOKEN",
      };
    }

    if (priceBoostOffer?.generosity) {
      return {
        message: i18n({
          key: "I18N.BOOST_VALUE_APPLIED",
          interpolationValues: {
            value: priceBoostOffer.generosity,
          },
        }),
        icon: ValueIconName.BOOSTER,
        type: "PRICE_BOOST_TOKEN",
      };
    }
  }

  if (isFreeBetsSelected) {
    const generosityAlertMessage = !isSingleBetBetslip
      ? buildFreeBetsAlertMessage({
          localization,
          numLines,
          combinationAmountPerLine,
          combinationAmount,
        })
      : undefined;

    return { message: generosityAlertMessage, icon: ValueIconName.FREE_BET, type: "BONUS_CASH" };
  }

  if (isMoneyBackTokenSelected && moneyBackAmountLimit) {
    const formattedMaxReturn = currencyFormatWithDecimalPlaces({
      ...localization,
      value: moneyBackAmountLimit,
    });

    const moneyBackOffer = combination.moneyBackOffers?.find((offer) => offer.tokenId === combination.moneyBackTokenId);

    const message = pickMoneyBackAlertMessage({
      maxFinPos: moneyBackNumberOfPlaces,
      formattedAmount: formattedMaxReturn,
      isRefundWithCash: moneyBackOffer?.mode === MONEY_BACK_MODE.CASH,
    });

    return {
      message,
      icon: ValueIconName.MONEY_BACK,
      type: "MONEY_BACK_TOKEN",
    };
  }

  if (isGhostLegTokenSelected && ghostLegs) {
    if (ghostLegMaxStake) {
      const formattedMaxStake = currencyFormatWithDecimalPlaces({
        ...localization,
        value: ghostLegMaxStake,
        decimalPlaces: 2,
      });

      return {
        message: `${i18n({
          key: "I18N.GHOST_LEG_CONDITION",
          interpolationValues: {
            numberOfLegs: ghostLegs,
          },
        })}. ${i18n({
          key: "I18N.BETSLIP.VALIDATION_ABOVE_MAX_STAKE",
          interpolationValues: {
            maxStake: formattedMaxStake,
          },
        })}`,
        icon: ValueIconName.GHOST_LEG,
        type: "GHOST_LEG_TOKEN",
      };
    }

    return {
      message: i18n({
        key: "I18N.GHOST_LEG_CONDITION",
        interpolationValues: {
          numberOfLegs: ghostLegs,
        },
      }),
      icon: ValueIconName.GHOST_LEG,
      type: "GHOST_LEG_TOKEN",
    };
  }

  return {};
};

export const getGenerosityReceiptAlertData = ({
  combination,
  localization,
  isFreeBetsWalletsActive,
}: {
  combination: PlaceResult.PlacedCombination;
  localization: BetslipLocalization;
  isFreeBetsWalletsActive: boolean;
}): {
  message?: string;
  icon?: GenerosityIconName;
} => {
  const { hasBonusUsed, totalBonusUsed, isAccaInsuredToken, isPriceBoosted, hasMoneyBackUsed, hasGhostLeg } =
    combination;

  if (isFreeBetsWalletsActive && hasBonusUsed)
    return {
      message: buildFreeBetsAlertMessage({ localization, combinationAmount: totalBonusUsed }),
      icon: ValueIconName.FREE_BET,
    };
  if (isAccaInsuredToken)
    return {
      message: i18n({
        key: "I18N.MONEY_BACK_ACCA_APPLIED",
      }),
      icon: ValueIconName.MONEY_BACK,
    };
  if (isPriceBoosted)
    return {
      message: i18n({
        key: "I18N.BOOST_APPLIED",
      }),
      icon: ValueIconName.BOOSTER,
    };
  if (hasMoneyBackUsed) {
    return {
      message: i18n({
        key: "I18N.MONEY_BACK_REWARD.MONEY_BACK_APPLIED",
      }),
      icon: ValueIconName.MONEY_BACK,
    };
  }

  if (hasGhostLeg) {
    return {
      message: i18n({
        key: "I18N.GHOST_LEG_APPLIED",
      }),
      icon: ValueIconName.GHOST_LEG,
    };
  }

  return {};
};
